import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getStoreSettings, updateStoreSettings } from '../services/api';
import type { StoreSettings, UpdateStoreSettingsRequest } from '../types';

const SETTINGS_QUERY_KEY = ['store', 'settings'] as const;

/**
 * Hook for reading and updating store notification/account settings.
 *
 * GET  /api/store/settings  — returns email, tier, emailNotifyEnabled, etc.
 * PATCH /api/store/settings — updates emailNotifyEnabled
 */
export function useStoreSettings() {
  const queryClient = useQueryClient();

  const query = useQuery<StoreSettings>({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: getStoreSettings,
    staleTime: 60_000,
  });

  const mutation = useMutation<StoreSettings, Error, UpdateStoreSettingsRequest>({
    mutationFn: updateStoreSettings,
    // Optimistic update — apply immediately in the UI, roll back on error
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: SETTINGS_QUERY_KEY });
      const previous = queryClient.getQueryData<StoreSettings>(SETTINGS_QUERY_KEY);
      if (previous) {
        queryClient.setQueryData<StoreSettings>(SETTINGS_QUERY_KEY, {
          ...previous,
          ...variables,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Roll back to the snapshot from before the mutation
      const ctx = context as { previous?: StoreSettings } | undefined;
      if (ctx?.previous) {
        queryClient.setQueryData(SETTINGS_QUERY_KEY, ctx.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    },
  });

  return {
    settings: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    updateSettings: mutation.mutate,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
}
